import { createSlice, PayloadAction } from '@reduxjs/toolkit';

/**
 * B2B Intercept quiz state.
 *
 * Layered on top of the existing FindYourMoneyQuiz flow (which remains
 * pure useState internally). This slice tracks the cross-component
 * state that the intercept path needs:
 *
 *   - quizPhase — where the user is in the progressive-disclosure flow
 *     ('broad' → the 3 lifestyle screens, 'specific' → dynamically
 *      injected follow-ups like "Did you use talcum powder?",
 *      'evaluated' → backend has classified the user, and
 *      'finished' → intercept shown/declined, paywall or feed next)
 *
 *   - triggerAnswers — which broad-phase selections fired a drill-down,
 *     so the quiz can re-play injected questions on refresh without
 *     re-asking the user to pick again
 *
 *   - injectedQuestions — the follow-up questions appended mid-flow
 *     after a trigger fires; shape mirrors the QuizQuestion type the
 *     rest of the quiz uses (see FindYourMoneyQuiz.tsx)
 *
 *   - interceptResult — { intercept, matchTier, claimDetails } response
 *     from the /consumers/quiz/intercept endpoint. Null until evaluated.
 *
 *   - retainerSignedForClaimId — set after the user signs a retainer on
 *     an intercepted claim; drives the "continuation loop" copy on the
 *     PremiumPaywall.
 *
 * This slice is deliberately NOT persisted. The B2B intercept is a
 * single-session funnel — if the user refreshes they restart cleanly
 * rather than replaying a stale intercept that might no longer match
 * a live campaign.
 */

export type QuizPhase = 'broad' | 'specific' | 'evaluated' | 'finished';

export interface InjectedAnswerOption {
  id: string;
  answer: string;
  sublabel?: string;
  icon?: string;
  value?: string;
  claim_ids?: string[];
  /** If true, selecting this option is itself a B2B-qualifying signal. */
  trigger?: boolean;
}

export interface InjectedQuizQuestion {
  claimId: string;
  questionId: string;
  question: string;
  explainer: string;
  questionType: string;
  answerOptions: InjectedAnswerOption[];
  settlementValue?: string;
  step: string;
  /**
   * Which broad-phase triggerAnswer this follow-up was injected in
   * response to — e.g., "cancer-diagnosis" → "talcum-powder" question.
   * Used to remove orphaned injections if the user backs up and
   * deselects the trigger.
   */
  parentTrigger: string;
}

export interface InterceptClaimDetails {
  claimId: string;
  title: string;
  company?: string;
  lawsuitType?: string;
  estimatedPayout?: number | string;
  deadline?: string | null;
  /** Mass-tort retainer product type drives which LegalText template
   *  RetainerModal loads (mass-tort, mass-arbitration, personal-injury). */
  retainerType?: 'mass-tort' | 'mass-arbitration' | 'personal-injury';
}

export type InterceptTier = 'tier_1' | 'tier_2' | 'tier_3' | null;

export interface InterceptResult {
  intercept: boolean;
  matchTier: InterceptTier;
  claimDetails: InterceptClaimDetails | null;
  /** Server-returned campaign id for downstream ledger/attribution; the
   *  frontend never interprets this, just echoes it on /leads submit. */
  campaignId?: string | null;
  evaluatedAt: number;
}

/**
 * Preserves enough quiz state to let the user bounce OUT of the
 * intercept (decline retainer) and resume the progressive-disclosure
 * quiz from exactly where they left off. Without this, "keep looking"
 * would restart the quiz from question 1.
 */
export interface QuizResumeState {
  answerIds: string[];
  /** The next screen index to show on resume. When the user hit the
   *  intercept via early-exit at screen 7, this is 8. When they
   *  finished the whole quiz, this is >= total screen count and the
   *  quiz will auto-submit to the paywall instead of replaying. */
  nextScreenIndex: number;
  /** False when the user reached the end of the quiz naturally.
   *  True when they hit the mid-quiz early-exit interstitial. Used
   *  by the intercept screen to decide whether "not ready" should
   *  route back to more questions (true) or skip to paywall (false). */
  earlyExited: boolean;
}

export interface InterceptState {
  quizPhase: QuizPhase;
  triggerAnswers: string[];
  injectedQuestions: InjectedQuizQuestion[];
  interceptResult: InterceptResult | null;
  evaluating: boolean;
  evaluationError: string | null;
  retainerSignedForClaimId: string | null;
  quizResumeState: QuizResumeState | null;
  /** Lawsuit types the user has already been shown and either skipped
   *  or declined. Used to suppress the same match popping up a second
   *  time at the final quiz submit after the user already early-exited
   *  into it and chose "keep looking". Reset on fresh quiz entry. */
  seenLawsuitTypes: string[];
}

const initialState: InterceptState = {
  quizPhase: 'broad',
  triggerAnswers: [],
  injectedQuestions: [],
  interceptResult: null,
  evaluating: false,
  evaluationError: null,
  retainerSignedForClaimId: null,
  quizResumeState: null,
  seenLawsuitTypes: [],
};

const interceptSlice = createSlice({
  name: 'intercept',
  initialState,
  reducers: {
    setQuizPhase(state, action: PayloadAction<QuizPhase>) {
      state.quizPhase = action.payload;
    },
    /** Record that a broad-phase answer fired a drill-down, and queue
     *  its follow-up questions. Idempotent: re-adding the same trigger
     *  won't duplicate questions. */
    addTriggerAnswer(
      state,
      action: PayloadAction<{ trigger: string; questions: InjectedQuizQuestion[] }>,
    ) {
      const { trigger, questions } = action.payload;
      if (!state.triggerAnswers.includes(trigger)) {
        state.triggerAnswers.push(trigger);
      }
      const existingIds = new Set(state.injectedQuestions.map((q) => q.questionId));
      for (const q of questions) {
        if (!existingIds.has(q.questionId)) {
          state.injectedQuestions.push(q);
        }
      }
    },
    /** User backed up and deselected a trigger — drop its injections. */
    removeTriggerAnswer(state, action: PayloadAction<string>) {
      const trigger = action.payload;
      state.triggerAnswers = state.triggerAnswers.filter((t) => t !== trigger);
      state.injectedQuestions = state.injectedQuestions.filter(
        (q) => q.parentTrigger !== trigger,
      );
    },
    setEvaluating(state, action: PayloadAction<boolean>) {
      state.evaluating = action.payload;
      if (action.payload) state.evaluationError = null;
    },
    setInterceptResult(state, action: PayloadAction<InterceptResult>) {
      state.interceptResult = action.payload;
      state.evaluating = false;
      state.quizPhase = 'evaluated';
    },
    setEvaluationError(state, action: PayloadAction<string>) {
      state.evaluationError = action.payload;
      state.evaluating = false;
    },
    markRetainerSigned(state, action: PayloadAction<string>) {
      state.retainerSignedForClaimId = action.payload;
      state.quizPhase = 'finished';
    },
    /** Called by the preview page right before it routes the user to
     *  the intercept screen, so the intercept can offer a proper
     *  "resume" back into the quiz if the user declines the retainer. */
    setQuizResumeState(state, action: PayloadAction<QuizResumeState>) {
      state.quizResumeState = action.payload;
    },
    /** Clear the intercept match result but KEEP quizResumeState —
     *  used when the user declines the retainer and wants to go back
     *  into the quiz for more questions. Without this, a naive reset
     *  would dump the resume state too and restart at question 1.
     *
     *  Also tracks the lawsuit type of the declined match so the
     *  final quiz submit doesn't re-surface the same intercept. */
    clearInterceptMatchKeepResume(state) {
      if (state.interceptResult?.claimDetails?.lawsuitType) {
        const lt = state.interceptResult.claimDetails.lawsuitType;
        if (!state.seenLawsuitTypes.includes(lt)) {
          state.seenLawsuitTypes.push(lt);
        }
      }
      state.interceptResult = null;
      state.retainerSignedForClaimId = null;
      state.evaluating = false;
      state.evaluationError = null;
      state.quizPhase = 'broad';
    },
    /** Push one or more lawsuit types into the seen set. Used when
     *  the user declines the early-exit interstitial — we treat that
     *  as "I've seen this option, don't re-offer it" so the final
     *  end-of-quiz submit doesn't re-pop the same match. */
    addSeenLawsuitTypes(state, action: PayloadAction<string[]>) {
      for (const lt of action.payload) {
        if (lt && !state.seenLawsuitTypes.includes(lt)) {
          state.seenLawsuitTypes.push(lt);
        }
      }
    },
    /** Hard reset — call from StartPage on entry so a returning user
     *  doesn't replay a previous session's intercept result. */
    resetIntercept() {
      return initialState;
    },
  },
});

export const {
  setQuizPhase,
  addTriggerAnswer,
  removeTriggerAnswer,
  setEvaluating,
  setInterceptResult,
  setEvaluationError,
  markRetainerSigned,
  setQuizResumeState,
  clearInterceptMatchKeepResume,
  addSeenLawsuitTypes,
  resetIntercept,
} = interceptSlice.actions;

export default interceptSlice.reducer;
